/**
 * What the application knows about `workers/auth-api`.
 *
 * Four calls and one pure function, and the decisions in it are mostly about being honest when
 * the answer is "there is no account layer here". The calls and their parsers live in
 * `@orbit-mind/api-client`, shared with the phone app; this module binds them to the browser.
 *
 * **Every URL is relative.** The sign-in Worker is routed on the application's own origin — it
 * has to be, because a `__Host-` cookie carries no `Domain` and belongs to exactly one host — so
 * there is no base URL to configure and no environment variable that can be wrong. In development
 * `vite.config.ts` proxies `/auth` to a local `wrangler dev`.
 *
 * **Nothing here throws, and nothing here distinguishes failures the interface cannot act on.**
 * A 401, a network that is gone, and a dev server answering `index.html` to `/auth/session` all
 * mean the same thing to a screen: this person is not signed in. What *is* worth distinguishing
 * is whether a sign-in is possible at all, and `/auth/providers` is the one answer that says so —
 * which is why `readAccount` asks both questions and lets the second decide.
 *
 * **A sign-in is a navigation, not a request.** `signInHref` builds a URL and the interface puts
 * it in a link. The provider answers with a redirect to its own origin, and the handshake cookie
 * is only set on a top-level navigation, because it is `SameSite=Lax`. A `fetch` here would
 * follow the redirect in the background, set nothing, and end up nowhere.
 */
import { createApiClient, signInPath } from '@orbit-mind/api-client';
import { shareReaderId } from '../share/moderation';

export type { AccountProvider, AccountState, AccountUser } from '@orbit-mind/api-client';

/**
 * The browser's client: relative paths, and the cookie the browser attaches on its own.
 *
 * `fetch` is reached through `globalThis` on every call rather than bound once. A test replaces
 * the global between cases, and a reference captured at import would keep calling the old one.
 */
const client = createApiClient({
  fetch: async (input, init) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    if (!url.startsWith('/share/')) return globalThis.fetch(input, init);
    const headers = new Headers(init?.headers);
    headers.set('x-orbit-reader', await shareReaderId());
    return globalThis.fetch(input, { ...init, headers });
  },
});

export const readAccount = client.readAccount;
export const readPlan = client.readPlan;
export const offerInviteCode = client.offerInviteCode;
export const sendFeedback = client.sendFeedback;
export const signOut = client.signOut;
export const deleteAccount = client.deleteAccount;
export const importPdf = client.importPdf;
export const readShare = client.readShare;
export const share = client.share;
export const unshare = client.unshare;
export const openShared = client.openShared;

/** A link, not a request: the handshake cookie is only set on a top-level navigation. */
export const signInHref = signInPath;
